import React, { useEffect } from 'react';
import { useCompassHeading } from '../../hooks/useCompassHeading';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// 🎯 [UX 개선] 3D Floating Marker with Tail (50% 축소)
// - 부유감: 그림자와 본체를 분리하여 공중에 떠 있는 느낌
// - 정확성: 꼬리(Tail)를 추가하여 정확한 지점 표시
const createModernMarker = (colorType, iconSvg, isActive = false, heading = 0) => L.divIcon({
    className: isActive ? 'modern-marker-active' : 'modern-marker',
    html: `
    <div style="
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 26px; 
        height: 40px;
    ">
        <!-- 🎯 [NEW] Direction Arrow (heading이 있을 때만 표시) -->
        ${heading !== undefined && heading !== 0 ? `
        <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0; 
            height: 0; 
            border-left: 6px solid transparent;
            border-right: 6px solid transparent;
            border-bottom: 10px solid #3B82F6;
            transform-origin: center calc(100% + 25px); /* 본체 아래 중심 회전 */
            transform: translate(-50%, -50%) rotate(${heading}deg);
            z-index: 0;
            opacity: 0.8;
            pointer-events: none;
        "></div>
        ` : ''}

        <!-- 1. Floating 본체 & 꼬리 그룹 -->
        <div style="
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            z-index: 2;
            transition: transform 0.2s ease;
            ${isActive ? 'animation: floatBounceActive 1.5s infinite ease-in-out;' : 'animation: floatIdle 3s infinite ease-in-out;'}
        ">
            <!-- 구체 -->
            <div style="
                width: ${isActive ? '24px' : '20px'};
                height: ${isActive ? '24px' : '20px'};
                background: ${colorType.gradient};
                border: 1.5px solid #FFFFFF;
                border-radius: 50%;
                box-shadow: 
                    inset 0 2px 3px rgba(255,255,255,0.4),
                    inset 0 -2px 2px rgba(0,0,0,0.1),
                    0 2px 5px rgba(0,0,0,0.25);
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                ${iconSvg}
            </div>
            
            <!-- 꼬리 (역삼각형) -->
            <div style="
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-top: 7px solid #FFFFFF;
                margin-top: -1px;
                position: relative;
                z-index: -1;
                filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));
            ">
                <!-- 내부 색상 꼬리 -->
                <div style="
                    position: absolute;
                    top: -8px;
                    left: -3px;
                    width: 0;
                    height: 0;
                    border-left: 3px solid transparent;
                    border-right: 3px solid transparent;
                    border-top: 6px solid ${colorType.solid};
                "></div>
            </div>
        </div>

        <!-- 2. 바닥 그림자 -->
        <div style="
            position: absolute;
            bottom: 4px;
            width: ${isActive ? '16px' : '14px'};
            height: 5px;
            background: rgba(0,0,0,0.3);
            border-radius: 50%;
            filter: blur(1px);
            z-index: 1;
            ${isActive ? 'animation: shadowPulseActive 1.5s infinite ease-in-out;' : 'animation: shadowPulseIdle 3s infinite ease-in-out;'}
        "></div>
    </div>
    <style>
        @keyframes floatIdle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-1px); }
        }
        @keyframes shadowPulseIdle {
            0%, 100% { transform: scale(1); opacity: 0.35; }
            50% { transform: scale(0.95); opacity: 0.25; }
        }
        @keyframes floatBounceActive {
            0%, 100% { transform: translateY(0) scale(1.05); }
            50% { transform: translateY(-2px) scale(1.05); } 
        }
        @keyframes shadowPulseActive {
            0%, 100% { transform: scale(1.1); opacity: 0.4; }
            50% { transform: scale(0.9); opacity: 0.3; }
        }
    </style>
    `,
    iconSize: [26, 40],
    iconAnchor: [13, 38],
    popupAnchor: [0, -34]
});

// 🎨 SVG 아이콘 (흰색 fill로 통일) - 12px (50% 축소)
// 🎯 [UX 개선] Store 아이콘 변경: 방패 + 체크 (Verified Store 느낌)
const storeIconSvg = `<svg viewBox="0 0 24 24" width="12" height="12" fill="#FFFFFF" style="filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/></svg>`;
const postIconSvg = `<svg viewBox="0 0 24 24" width="12" height="12" fill="#FFFFFF" style="filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
const landmarkIconSvg = `<svg viewBox="0 0 24 24" width="12" height="12" fill="#FFFFFF" style="filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));"><path d="M12 2L2 7v2h20V7L12 2zm0 2.5L18 7H6l6-2.5zM4 11v9h3v-6h2v6h2v-6h2v6h2v-6h2v6h3v-9H4z"/></svg>`;

// 🎨 색상 정의: 3D 입체감 (Gradient) + 꼬사용 단색 (Solid)
const MARKER_COLORS = {
    store: {
        gradient: 'linear-gradient(135deg, #F87171 0%, #EF4444 60%, #DC2626 100%)',
        solid: '#EF4444'
    },
    footprint: {
        gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 60%, #D97706 100%)',
        solid: '#F59E0B'
    },
    landmark: {
        gradient: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 60%, #2563EB 100%)',
        solid: '#3B82F6'
    }
};

// 아이콘 인스턴스
const storeIcon = createModernMarker(MARKER_COLORS.store, storeIconSvg);
const postIcon = createModernMarker(MARKER_COLORS.footprint, postIconSvg);
const landmarkIcon = createModernMarker(MARKER_COLORS.landmark, landmarkIconSvg);
const storeIconActive = createModernMarker(MARKER_COLORS.store, storeIconSvg, true);
const postIconActive = createModernMarker(MARKER_COLORS.footprint, postIconSvg, true);
const landmarkIconActive = createModernMarker(MARKER_COLORS.landmark, landmarkIconSvg, true);

// 🎯 내 위치 마커 - 동적 생성 함수 (Heading 지원)
const createMyLocationIcon = (heading = 0) => {
    const showHeadingArrow = heading !== 0;
    // 🎯 [FIX] Navigational Heading (0=North, 90=East) -> CSS Rotation (0=Up/North)
    const cssHeading = heading;
    return L.divIcon({
        className: 'my-location-marker',
        html: `<div style="position: relative; width: 40px; height: 40px;">
            <!-- 🎯 [FIX] Heading Arrow - 원과 밀착, 길이 축소 -->
            ${showHeadingArrow ? `
            <div style="
                position: absolute;
                top: 50%; left: 50%;
                width: 0; height: 0;
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-bottom: 14px solid #3B82F6;
                transform: translate(-50%, -18px) rotate(${cssHeading}deg);
                transform-origin: center calc(100% + 8px);
            "></div>
            ` : ''}
            <!-- 🎯 [UX 개선] 펄스 링 애니메이션 -->
            <div style="
                position: absolute;
                top: 50%; left: 50%;
                transform: translate(-50%, -50%);
                width: 24px; height: 24px;
                background: rgba(79, 70, 229, 0.3);
                border-radius: 50%;
                animation: pulseRing 2s infinite ease-out;
            "></div>
            <!-- 중심 원 -->
            <div style="
                position: absolute;
                top: 50%; left: 50%;
                transform: translate(-50%, -50%);
                width: 16px; height: 16px;
                background: #4F46E5;
                border: 3px solid white;
                border-radius: 50%;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                z-index: 2;
            "></div>
            <style>
                @keyframes pulseRing {
                    0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
                    100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
                }
            </style>
        </div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
    });
};

// 🎯 Point Spot Icon (일반 선물상자 - 보라)
const pointSpotIcon = L.divIcon({
    className: 'point-spot-marker',
    html: `
        <div style="
            position: relative;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            filter: drop-shadow(0 4px 8px rgba(168, 85, 247, 0.4));
            animation: giftFloat 1.5s ease-in-out infinite;
        ">
            <div style="
                position: absolute;
                width: 40px;
                height: 40px;
                top: -4px;
                left: -4px;
                border-radius: 50%;
                background: radial-gradient(circle, rgba(168, 85, 247, 0.5) 0%, transparent 70%);
                filter: blur(4px);
                animation: glowPulse 2s ease-in-out infinite;
                z-index: 1;
            "></div>
            <div style="font-size: 20px; line-height: 20px; position: relative; z-index: 2;">🎁</div>
            <div style="position: absolute; top: -6px; right: -2px; font-size: 14px; z-index: 3; animation: sparkle 1.2s ease-in-out infinite;">✨</div>
            <div style="position: absolute; bottom: -2px; left: -6px; font-size: 10px; z-index: 3; animation: sparkle 1.5s ease-in-out infinite 0.5s;">✨</div>
            <style>
                @keyframes giftFloat { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-5px) scale(1.05); } }
                @keyframes sparkle { 0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); } 50% { opacity: 1; transform: scale(1.2) rotate(15deg); } }
                @keyframes glowPulse { 0%, 100% { opacity: 0.5; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.2); } }
            </style>
        </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16]
});

// 🎯 랜드마크 주변 선물상자 — 파란색 파스텔
// 글로우 모양/강도는 일반 선물상자와 동일하게, 흰색 외곽선(테두리)은 제거
const landmarkPointSpotIcon = L.divIcon({
    className: 'landmark-point-spot-marker',
    html: `
        <div style="
            position: relative;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            filter: drop-shadow(0 4px 8px rgba(96, 165, 250, 0.4));
            border-radius: 50%;
            animation: giftFloat 1.5s ease-in-out infinite;
        ">
            <div style="
                position: absolute;
                width: 40px;
                height: 40px;
                top: -4px;
                left: -4px;
                border-radius: 50%;
                background: radial-gradient(circle, rgba(96, 165, 250, 0.5) 0%, transparent 70%);
                filter: blur(4px);
                animation: glowPulse 2s ease-in-out infinite;
                z-index: 1;
            "></div>
            <div style="font-size: 20px; line-height: 20px; position: relative; z-index: 2; filter: hue-rotate(180deg) saturate(0.9) brightness(1.05);">🎁</div>
            <div style="position: absolute; top: -6px; right: -2px; font-size: 12px; z-index: 3; opacity: 0.9; animation: sparkle 1.2s ease-in-out infinite;">✨</div>
            <div style="position: absolute; bottom: -2px; left: -6px; font-size: 10px; z-index: 3; opacity: 0.9; animation: sparkle 1.5s ease-in-out infinite 0.5s;">✨</div>
            <style>
                @keyframes giftFloat { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-5px) scale(1.05); } }
                @keyframes sparkle { 0%, 100% { opacity: 0.5; transform: scale(0.5) rotate(0deg); } 50% { opacity: 1; transform: scale(1.2) rotate(15deg); } }
                @keyframes glowPulse { 0%, 100% { opacity: 0.7; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
            </style>
        </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15]
});

// Map Controller for dynamic updates
const MapController = ({ center, mapTarget, isExpanded, selectedSpotId, spots, myPos, setOffScreenIndicator, onViewChange, distanceRadius, onMapReady }) => {
    const map = useMap();
    const prevRadiusRef = React.useRef(null); // 🎯 [FIX] null로 시작하여 첫 마운트 시 줌 적용 보장

    // 1. External Target Pan
    useEffect(() => {
        if (!mapTarget) return; // 🎯 [FIX] null 체크

        // Support both array [lat, lng, timestamp] and object { lat, lng, zoom, timestamp }
        const isObject = typeof mapTarget === 'object' && !Array.isArray(mapTarget);
        const lat = isObject ? mapTarget.lat : mapTarget[0];
        const lng = isObject ? mapTarget.lng : mapTarget[1];
        const zoom = isObject ? mapTarget.zoom : null;

        if (lat && lng) {
            if (zoom !== null && zoom !== undefined) {
                // 🎯 [Naver Sync] 명시적 zoom 적용 시 prevRadiusRef 리셋
                // 이후 distanceRadius 변경이 정상 동작하도록 함
                map.setView([lat, lng], zoom);
                prevRadiusRef.current = null;
            } else {
                map.setView([lat, lng], map.getZoom());
            }
        }
    }, [mapTarget, map]);

    // 2. Selected Spot Pan
    const lastPannedId = React.useRef(null); // 🎯 [FIX] Prevent snapping back

    useEffect(() => {
        if (selectedSpotId && spots.length > 0) {
            // 이미 이동했다면 스킵
            if (lastPannedId.current === selectedSpotId) return;

            const spot = spots.find(s => s.id === selectedSpotId);
            if (spot && spot.geo) {
                map.flyTo([spot.geo.lat, spot.geo.lng], 16, { duration: 0.8 });
                lastPannedId.current = selectedSpotId;
            }
        }
    }, [selectedSpotId, spots, map]);

    // 3. Resize Handle
    useEffect(() => {
        setTimeout(() => map.invalidateSize(), 300);
    }, [isExpanded, map]);

    // 4. Initial Center
    useEffect(() => {
        if (center) {
            // Only set view if not already moved? Or force update?
            // Usually center is passed for initial load or myPos update.
            // map.setView([center.lat, center.lng], map.getZoom());
        }
    }, [center, map]);

    // 🎯 [Tracking Mode] State for "Free Mode" vs "Follow Mode"
    const isTrackingRef = React.useRef(true); // Default: Follow User

    // Expose setTrackingMode to parent via map instance
    useEffect(() => {
        if (!map) return;

        map.setTrackingMode = (enabled) => {
            isTrackingRef.current = enabled;
            // Immediate re-center if enabling tracking?
            if (enabled && myPos) {
                map.setView([myPos.lat, myPos.lng], map.getZoom(), { animate: true });
            }
        };

        // 🎯 [Interaction] User Drag -> Disable Tracking (Free Mode)
        map.on('dragstart', () => {
            isTrackingRef.current = false;
        });

        // 🎯 [Initial] Notify Parent
        if (onMapReady) onMapReady(map);

    }, [map, onMapReady, myPos]); // myPos dependency for immediate centring in setTrackingMode closure, might need ref for myPos to avoid stale closure if setTrackingMode is called later. Actually better use ref for myPos or just re-attach.

    // 🎯 [NEW] Distance Radius -> Zoom Level (Centered on User)
    // BUT: Don't override if mapTarget has explicit zoom (e.g., from treasure hunt mode)
    useEffect(() => {
        if (!distanceRadius || !myPos) return;

        // Only auto-zoom if Tracking is ON ?? Or always update zoom but only center if tracking?
        // Actually, distanceRadius change implies we WANT to see that radius. So we should probably force center?
        // But if user is exploring... 
        // Let's stick to: if Tracking is ON, we center. 

        if (distanceRadius === prevRadiusRef.current) return;

        // Check if mapTarget has explicit zoom - if so, don't override
        const isObject = typeof mapTarget === 'object' && !Array.isArray(mapTarget);
        const hasExplicitZoom = isObject && mapTarget?.zoom !== null && mapTarget?.zoom !== undefined;

        if (hasExplicitZoom) {
            console.log('[LeafletMap] Skipping distanceRadius zoom - mapTarget has explicit zoom');
            return;
        }

        // Zoom levels calibrated to show radius on screen
        let z = 13;
        if (distanceRadius <= 1000) z = 14;       // 1km - closer view
        else if (distanceRadius <= 5000) z = 12;  // 5km
        else if (distanceRadius <= 10000) z = 11; // 10km
        else if (distanceRadius <= 50000) z = 9;  // 50km - wider view
        else z = 8;

        // Center on user's position with new zoom
        if (isTrackingRef.current) {
            map.setView([myPos.lat, myPos.lng], z, { animate: true, duration: 0.5 });
        }
        prevRadiusRef.current = distanceRadius;
    }, [distanceRadius, myPos, map, mapTarget]);

    // 5. Off-screen Indicator
    const prevIndicator = React.useRef({ angle: null, distance: null }); // 🎯 [FIX] infinite loop prevention

    useEffect(() => {
        if (!myPos || !setOffScreenIndicator) return;

        const updateIndicator = () => {
            const bounds = map.getBounds();
            const myLatLng = L.latLng(myPos.lat, myPos.lng);

            let newState = { angle: null, distance: null };

            if (!bounds.contains(myLatLng)) {
                const center = map.getCenter();
                const centerPt = map.latLngToContainerPoint(center);
                const myPt = map.latLngToContainerPoint(myLatLng);

                const dx = myPt.x - centerPt.x;
                const dy = myPt.y - centerPt.y;
                const angle = Math.atan2(dy, dx) * (180 / Math.PI);

                const distMeters = map.distance(center, myLatLng);
                const distStr = distMeters < 1000 ? `${Math.round(distMeters)}m` : `${(distMeters / 1000).toFixed(1)}km`;

                newState = {
                    angle: angle + 90,
                    distance: distStr
                };
            }

            // 🎯 [FIX] Only update if changed significantly
            const prev = prevIndicator.current;
            const isDifferent = prev.distance !== newState.distance || Math.abs((prev.angle || 0) - (newState.angle || 0)) > 1;

            if (isDifferent) {
                prevIndicator.current = newState;
                setOffScreenIndicator(newState);
            }
        };

        map.on('move', updateIndicator);
        map.on('zoom', updateIndicator);

        // Initial check
        updateIndicator();

        return () => {
            map.off('move', updateIndicator);
            map.off('zoom', updateIndicator);
        };
    }, [map, myPos, setOffScreenIndicator]);

    // 6. 🎯 [Real Follow Mode Setup]
    useEffect(() => {
        if (isTrackingRef.current && myPos) {
            map.panTo([myPos.lat, myPos.lng], { animate: true, duration: 0.5 });
        }
    }, [myPos, map]);

    return null;

};



/**
 * 🌍 LeafletMapAdapter - 글로벌 지도 (OSM 기반)
 */
const LeafletMapAdapter = ({
    center,
    spots = [],
    selectedSpotId,
    onSpotClick,
    myPos,
    isExpanded,
    mapTarget,
    setOffScreenIndicator,
    initialViewState,
    onViewChange,
    distanceRadius, // 🎯 [NEW] Zoom based on distance
    pointSpots = [], // 🎯 [NEW]
    onPointSpotClick, // 🎯 [NEW]
    onMapReady // 🎯 [FIX] Add missing prop
}) => {
    // 🎯 [FIX] Callback Refs to prevent stale closures
    const onSpotClickRef = React.useRef(onSpotClick);
    const onPointSpotClickRef = React.useRef(onPointSpotClick);

    useEffect(() => {
        onSpotClickRef.current = onSpotClick;
        onPointSpotClickRef.current = onPointSpotClick;
    }, [onSpotClick, onPointSpotClick]);

    // 🎯 [Heading] New Robust Compass Hook
    const compassHeading = useCompassHeading();
    const isCompassActive = compassHeading !== 0; // Simple check

    // Removed old verify logic

    // 🎯 [Heading Logic]
    const prevHeadingRef = React.useRef(0);

    // Fuse Heading
    let displayHeading = prevHeadingRef.current;
    if (myPos) {
        const speed = myPos.speed || 0;
        const useGpsHeading = speed > 0.5 && myPos.heading !== undefined;

        if (useGpsHeading) {
            let diff = myPos.heading - prevHeadingRef.current;
            if (diff > 180) diff -= 360;
            if (diff < -180) diff += 360;
            displayHeading = prevHeadingRef.current + (diff * 0.25);
            displayHeading = prevHeadingRef.current + (diff * 0.25);
        } else {
            // 🎯 [Fix] Only use compass if active, otherwise keep last GPS heading
            if (isCompassActive) {
                displayHeading = compassHeading;
            } else {
                displayHeading = prevHeadingRef.current;
            }
        }
        prevHeadingRef.current = (displayHeading + 360) % 360;
    }


    const getIcon = (spot, isSelected) => {
        if (spot.type === 'store') return isSelected ? storeIconActive : storeIcon;
        if (spot.type === 'footprint') return isSelected ? postIconActive : postIcon;
        return isSelected ? landmarkIconActive : landmarkIcon;
    };

    if (!center && !myPos && !initialViewState && !(mapTarget && mapTarget.lat)) return null;

    // 🎯 [FIX] Naver와 동일한 초기 줌 계산 로직
    // PRIORITY: mapTarget.zoom > initialViewState.zoom > distanceRadius
    const targetCenter = (mapTarget && typeof mapTarget === 'object' && !Array.isArray(mapTarget) && mapTarget.lat)
        ? [mapTarget.lat, mapTarget.lng]
        : null;
    const startCenter = (initialViewState?.center && initialViewState?.center.lat)
        ? [initialViewState.center.lat, initialViewState.center.lng]
        : (center ? [center.lat, center.lng] : (myPos ? [myPos.lat, myPos.lng] : (targetCenter || [37.5665, 126.9780])));

    // Calculate initial zoom with same priority as Naver
    const targetZoom = (mapTarget && typeof mapTarget === 'object' && !Array.isArray(mapTarget)) ? mapTarget.zoom : null;
    let startZoom = 13;

    if (targetZoom !== null && targetZoom !== undefined) {
        startZoom = targetZoom;
    } else if (initialViewState?.zoom) {
        startZoom = initialViewState.zoom;
    } else if (distanceRadius) {
        // Same zoom levels as Naver's getZoomLevel helper
        if (distanceRadius <= 1000) startZoom = 14;
        else if (distanceRadius <= 5000) startZoom = 12;
        else if (distanceRadius <= 10000) startZoom = 11;
        else if (distanceRadius <= 50000) startZoom = 9;
        else startZoom = 8;
    }

    return (
        <MapContainer
            center={startCenter}
            zoom={startZoom}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
        >
            <MapController
                // center={myPos} // Removed to avoid conflict
                myPos={myPos}
                mapTarget={mapTarget}
                isExpanded={isExpanded} // 🎯 [FIX] Use actual prop value
                selectedSpotId={selectedSpotId}
                spots={spots}
                setOffScreenIndicator={setOffScreenIndicator}
                onViewChange={onViewChange}
                distanceRadius={distanceRadius} // 🎯 [NEW] Zoom based on distance
                onMapReady={onMapReady} // 🎯 [NEW] Expose map instance
            />

            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* 내 위치 마커 (Heading 화살표 지원) */}
            {myPos && (
                <Marker position={[myPos.lat, myPos.lng]} icon={createMyLocationIcon(displayHeading || 0)} interactive={false} zIndexOffset={100} />
            )}

            {/* 스팟 마커들 - Popup 제거 (Naver와 동일하게 onSpotClick으로 모달 처리) */}
            {spots.filter(s => s.geo).map(spot => {
                const isSelected = spot.id === selectedSpotId;
                return (
                    <Marker
                        key={spot.id}
                        position={[spot.geo.lat, spot.geo.lng]}
                        icon={getIcon(spot, isSelected)}
                        opacity={spot.status === 'dormant' ? 0.5 : 1}
                        eventHandlers={{
                            click: (e) => {
                                L.DomEvent.stopPropagation(e.originalEvent || e);
                                onSpotClickRef.current && onSpotClickRef.current(spot);
                            }
                        }}
                    />
                );
            })}

            {/* 🎯 Point Spot Markers (일반 보라 / 랜드마크 골드) */}
            {pointSpots.map(spot => (
                <Marker
                    key={spot.id}
                    position={[spot.lat, spot.lng]}
                    icon={spot.isLandmarkPointSpot ? landmarkPointSpotIcon : pointSpotIcon}
                    zIndexOffset={300}
                    eventHandlers={{
                        click: (e) => {
                            L.DomEvent.stopPropagation(e.originalEvent || e);
                            onPointSpotClickRef.current && onPointSpotClickRef.current(spot);
                        }
                    }}
                >
                    {/* Optional: Add a small popup or just click to open modal handled by parent */}
                </Marker>
            ))}
        </MapContainer>
    );
};

export default React.memo(LeafletMapAdapter);

